import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SubscriptionGuard - Protects routes based on subscription status
 * 
 * Behavior:
 * - If subscription is active: Allow access to children
 * - If subscription expired/cancelled: Redirect to /app/resubscribe
 * - Allows access to resubscribe page without subscription
 */
export default function SubscriptionGuard({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Debug: Log what we're receiving
    console.log('[SubscriptionGuard] User object:', user);
    console.log('[SubscriptionGuard] coach_id present:', !!user?.coach_id);
    
    // Don't check subscription on resubscribe page or login/signup pages
    const publicPaths = ['/app/resubscribe', '/app/login', '/app/signup', '/app/signup-checkout', '/app/signup-success'];
    if (publicPaths.some(path => location.pathname.startsWith(path))) {
      setHasAccess(true);
      setChecking(false);
      return;
    }

    // If coach_id is missing, fetch fresh user data from API (might be old localStorage)
    if (user && !user.coach_id) {
      console.log('[SubscriptionGuard] coach_id missing, fetching fresh user data...');
      axios.get(`${API}/auth/me`)
        .then(response => {
          const freshUser = response.data;
          console.log('[SubscriptionGuard] Fresh user data:', freshUser);
          
          if (freshUser.coach_id) {
            console.log('[SubscriptionGuard] ✅ Fresh data has coach_id - FREE access granted!');
            // Update localStorage with fresh data
            localStorage.setItem('client_user', JSON.stringify(freshUser));
            setHasAccess(true);
            setChecking(false);
          } else {
            console.log('[SubscriptionGuard] No coach_id in fresh data, checking subscription...');
            checkSubscriptionStatus();
          }
        })
        .catch(err => {
          console.error('[SubscriptionGuard] Error fetching fresh user:', err);
          checkSubscriptionStatus();
        });
      return;
    }

    // Coach-invited / signed-up clients have a coach_id. We still need to gate them
    // if their 7-day trial has expired AND they don't have an active subscription.
    if (user?.coach_id) {
      const trialActive = !user.is_trial || (user.trial_expires_at && new Date(user.trial_expires_at) > new Date());
      const subActive = user.subscription_status === 'active';

      if (subActive || trialActive) {
        console.log('[SubscriptionGuard] Trial active or paid sub - access granted');
        setHasAccess(true);
        setChecking(false);
        return;
      }
      // Trial expired and no active sub — redirect to resubscribe
      console.log('[SubscriptionGuard] Trial expired, redirecting to /app/resubscribe');
      navigate('/app/resubscribe', { replace: true });
      setHasAccess(false);
      setChecking(false);
      return;
    }

    checkSubscriptionStatus();
  }, [location.pathname, user]);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      const subscription = response.data;

      // Check if subscription is active
      const isActive = subscription.has_subscription && 
                      subscription.status === 'active' && 
                      !subscription.cancel_at_period_end;

      if (isActive) {
        setHasAccess(true);
      } else {
        // Subscription expired or cancelled - redirect to resubscribe
        console.log('[SubscriptionGuard] Subscription not active, redirecting to resubscribe');
        navigate('/app/resubscribe', { replace: true });
        setHasAccess(false);
      }
    } catch (error) {
      console.error('[SubscriptionGuard] Error checking subscription:', error);
      
      // If error is 401 (unauthorized), let the main auth system handle it
      if (error.response?.status === 401) {
        setHasAccess(false);
      } else {
        // For other errors, allow access (fail-open to avoid locking users out)
        setHasAccess(true);
      }
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null; // Navigation will happen via useEffect
  }

  return children;
}
